import React from "react";
import { useNavigate } from "react-router-dom";
import { fontCss, radiusFor, sectionAnchor, sectionMeta } from "@/web/sections";

// Phase 82 — thème du site public (variables CSS) + helpers d'affichage
export function luminance(hex) {
  const h = String(hex || "#000").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16) / 255);
  const f = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export const onColor = (hex) => (luminance(hex) > 0.45 ? "#0A0A0A" : "#FFFFFF");

export function themeStyle(theme = {}) {
  const primary = theme.primary || "#002FA7", accent = theme.accent || "#FFD700", bg = theme.bg || "#FFFFFF", text = theme.text || "#0A0A0A";
  const light = luminance(bg) > 0.5;
  return {
    "--w-primary": primary, "--w-accent": accent, "--w-bg": bg, "--w-text": text, "--w-on-primary": onColor(primary), "--w-on-accent": onColor(accent),
    "--w-radius": radiusFor(theme.buttons), "--w-muted": light ? "rgba(10,10,10,0.05)" : "rgba(255,255,255,0.07)", "--w-border": light ? "rgba(10,10,10,0.12)" : "rgba(255,255,255,0.16)",
    "--w-card": light ? "#FFFFFF" : "rgba(255,255,255,0.04)", fontFamily: fontCss(theme.font), background: bg, color: text,
  };
}

// Liens internes : `#contact` (ancre, même page ou autre page), `/prestations` (page), sinon URL externe
const EXTERNAL = /^(https?:\/\/|mailto:|tel:|\/\/)/i;
export function resolveLink(link, nav = {}) {
  if (!link) return "#";
  if (EXTERNAL.test(link)) return link;
  const { sections = [], pages = [], base = "", search = "" } = nav;
  const findIn = (secs, key) => (secs || []).find((s) => sectionMeta(s.type).anchor === key || s.type === key || sectionAnchor(s) === key);
  const pagePath = (p) => `${base}${p.slug ? `/${p.slug}` : ""}${search}` || "/";
  if (link.startsWith("#")) {
    const key = link.slice(1);
    const here = findIn(sections, key);
    if (here) return `#${sectionAnchor(here)}`;
    for (const p of pages) { const t = findIn(p.sections, key); if (t) return `${pagePath(p)}#${sectionAnchor(t)}`; }
    return link;
  }
  if (link.startsWith("/")) {
    const slug = link.replace(/^\/+/, "").split(/[?#]/)[0];
    const p = pages.find((x) => x.slug === slug);
    return p ? pagePath(p) : link;
  }
  return link;
}

export const isInternal = (href) => !!href && !EXTERNAL.test(href) && !href.startsWith("#");

export function scrollToAnchor(href) {
  if (!href?.startsWith("#")) return;
  const el = document.getElementById(href.slice(1));
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Clic sur un lien de section : ancre → scroll, page interne → navigation SPA (conserve ?preview=1), sinon navigateur
export function useSiteLink() {
  const navigate = useNavigate();
  return (e, href) => {
    if (!href || href === "#") { e.preventDefault(); return; }
    if (href.startsWith("#")) { e.preventDefault(); scrollToAnchor(href); return; }
    if (isInternal(href)) { e.preventDefault(); navigate(href); }
  };
}

const VARIANTS = {
  primary: { background: "var(--w-primary)", color: "var(--w-on-primary)" },
  accent: { background: "var(--w-accent)", color: "var(--w-on-accent)" },
  outline: { border: "2px solid currentColor", background: "transparent", color: "inherit" },
  dark: { background: "#0A0A0A", color: "#FFFFFF" },
  light: { background: "#FFFFFF", color: "#0A0A0A" },
};

export function WButton({ as: Tag = "button", variant = "primary", size = "md", className = "", style, children, ...rest }) {
  const h = size === "sm" ? "h-9 px-4 text-xs" : size === "lg" ? "h-13 px-8 text-base" : "h-11 px-6 text-sm";
  return (
    <Tag className={`inline-flex items-center justify-center gap-2 font-bold whitespace-nowrap transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0 ${h} ${className}`}
         style={{ borderRadius: "var(--w-radius)", ...VARIANTS[variant], ...style }} {...rest}>{children}</Tag>
  );
}

export function SectionWrap({ section, children, className = "", narrow = false, muted = false }) {
  return (
    <section id={sectionAnchor(section)} data-testid={`ws-${section.type}`} className={`px-6 lg:px-10 py-16 lg:py-24 scroll-mt-20 ${className}`} style={muted ? { background: "var(--w-muted)" } : undefined}>
      <div className={`${narrow ? "max-w-3xl" : "max-w-[1200px]"} mx-auto`}>
        {(section.title || section.subtitle) && (
          <header className="mb-10 lg:mb-14 max-w-2xl">
            <div className="h-1 w-12 mb-5" style={{ background: "var(--w-accent)" }} />
            {section.title && <h2 className="text-3xl lg:text-4xl font-black tracking-tight">{section.title}</h2>}
            {section.subtitle && <p className="mt-3 text-base lg:text-lg opacity-70">{section.subtitle}</p>}
          </header>
        )}
        {children}
      </div>
    </section>
  );
}

export function Stars({ n = 0, className = "h-4 w-4" }) {
  return <span className="inline-flex gap-0.5" aria-label={`${n} / 5`}>{[1, 2, 3, 4, 5].map((i) => <svg key={i} viewBox="0 0 20 20" className={className} fill={i <= n ? "var(--w-accent)" : "none"} stroke={i <= n ? "var(--w-accent)" : "currentColor"} strokeWidth="1.5"><path d="M10 1.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L10 14.9l-5.3 2.8 1.1-5.9L1.5 7.7l5.9-.8z" /></svg>)}</span>;
}
